import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { useAuth } from "../App";
import { toast } from "sonner";
import {
  Building2, ArrowLeft, Star, DollarSign, Heart, Send, Loader2,
  CheckCircle, Gift, Sparkles
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIP_PRESETS = [10, 20, 50, 100];

export default function TipPage() {
  const { contractId } = useParams();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [contract, setContract] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [tipAmount, setTipAmount] = useState("");
  const [tipMessage, setTipMessage] = useState("");
  const [tipSent, setTipSent] = useState(false);

  useEffect(() => {
    fetchContract();
  }, [contractId, token]);

  const fetchContract = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contractId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        setContract(await response.json());
      }
    } catch (error) {
      console.error("Error fetching contract:", error);
    } finally {
      setLoading(false);
    }
  };

  const sendTip = async () => {
    const amount = parseFloat(tipAmount);
    if (!amount || amount < 5) {
      toast.error("Valor mínimo da gorjeta é R$ 5,00");
      return;
    }
    if (amount > 1000) {
      toast.error("Valor máximo da gorjeta é R$ 1.000,00");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/tips`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          contract_id: contractId,
          amount: amount,
          message: tipMessage || null
        })
      });

      if (response.ok) {
        setTipSent(true);
        toast.success("Gorjeta enviada com sucesso!");
      } else {
        const error = await response.json();
        throw new Error(error.detail || "Erro ao enviar gorjeta");
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="w-12 h-12 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (tipSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-emerald-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="p-8">
            <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Heart className="w-10 h-10 text-emerald-500 fill-emerald-500" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Obrigado pela gorjeta!</h2>
            <p className="text-slate-600 mb-6">
              Sua generosidade foi enviada ao prestador. Gestos assim fazem toda a diferença!
            </p>
            <div className="p-4 bg-emerald-50 rounded-lg mb-6">
              <p className="text-sm text-emerald-600">Valor enviado</p>
              <p className="text-3xl font-bold text-emerald-700">
                R$ {parseFloat(tipAmount).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </p>
            </div>
            <Button className="w-full" onClick={() => navigate("/contracts")}>
              Voltar aos Contratos
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50" data-testid="tip-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-emerald-600" />
                <span className="font-semibold text-slate-900">Enviar Gorjeta</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {/* Hero */}
        <Card className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white overflow-hidden">
          <CardContent className="p-6 text-center">
            <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-80" />
            <h1 className="text-2xl font-bold mb-2">Serviço Excepcional?</h1>
            <p className="text-emerald-100">
              Mostre sua gratidão com uma gorjeta digital. 100% vai para o prestador!
            </p>
          </CardContent>
        </Card>

        {/* Contract Info */}
        {contract && (
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Contrato</p>
                  <p className="font-semibold text-slate-900">#{contract.contract_id?.slice(-8)}</p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Valor do Serviço</p>
                  <p className="font-semibold text-slate-900">
                    R$ {contract.total_value?.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                  </p>
                </div>
              </div>
              {contract.live_rating_avg > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                  <span className="font-medium">{contract.live_rating_avg.toFixed(1)}</span>
                  <span className="text-slate-500 text-sm">avaliação do serviço</span>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Tip Amount */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Valor da Gorjeta
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Preset Amounts */}
            <div className="grid grid-cols-4 gap-2">
              {TIP_PRESETS.map((preset) => (
                <Button
                  key={preset}
                  variant={tipAmount === preset.toString() ? "default" : "outline"}
                  className={tipAmount === preset.toString() ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                  onClick={() => setTipAmount(preset.toString())}
                  data-testid={`tip-preset-${preset}`}
                >
                  R$ {preset}
                </Button>
              ))}
            </div>

            {/* Custom Amount */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Ou digite um valor personalizado:
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">R$</span>
                <Input
                  type="number"
                  value={tipAmount}
                  onChange={(e) => setTipAmount(e.target.value)}
                  className="pl-10 text-lg"
                  placeholder="0,00"
                  min="5"
                  max="1000"
                  data-testid="tip-amount-input"
                />
              </div>
              <p className="text-xs text-slate-500 mt-1">Mínimo: R$ 5,00 • Máximo: R$ 1.000,00</p>
            </div>

            {/* Message */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Mensagem (opcional):
              </label>
              <Textarea
                value={tipMessage}
                onChange={(e) => setTipMessage(e.target.value)}
                placeholder="Obrigado pelo excelente trabalho!"
                rows={3}
                data-testid="tip-message-input"
              />
            </div>

            {/* Send Button */}
            <Button 
              className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg"
              onClick={sendTip}
              disabled={submitting || !tipAmount}
              data-testid="send-tip-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              ) : (
                <Heart className="w-5 h-5 mr-2" />
              )}
              Enviar Gorjeta de R$ {tipAmount || "0"}
            </Button>
          </CardContent>
        </Card>

        {/* Info */}
        <p className="text-center text-sm text-slate-500">
          A gorjeta é 100% destinada ao prestador, sem taxas da plataforma.
        </p>
      </main>
    </div>
  );
}
